import { useState, useEffect, useRef } from "react";
import { Field, TAGLIE, blurSuTouch } from "@/components/Shared";
import { formattaNome, etaAnimatoValida, ETA_MIN_ANIMATO, ETA_MAX_ANIMATO } from "@/lib/utils";
import { Plus, X, HeartPulse, Info, AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, UserRound } from "lucide-react";
import InformativaBox from "@/components/InformativaBox";

const RELAZIONI_COMPILANTE = [
  ["madre", "Madre"], ["padre", "Padre"], ["tutore", "Tutore"],
  ["altro_esercente", "Altro esercente la responsabilità genitoriale"],
];

const RELAZIONI_CONTATTO = [
  ["madre", "Madre"], ["padre", "Padre"], ["nonno", "Nonno"], ["nonna", "Nonna"],
  ["zio", "Zio"], ["zia", "Zia"], ["fratello", "Fratello"], ["sorella", "Sorella"],
  ["tutore", "Tutore"], ["altro", "Altro"],
];

const ESIGENZE = [
  ["senza_glutine", "Senza glutine"], ["senza_lattosio", "Senza lattosio"],
  ["vegetariano", "Vegetariano"], ["altro", "Altro (specificare)"],
];

// Consensi facoltativi, separati e tutti a `null` all'apertura: nessuna
// casella preselezionata. Una casella già spuntata non è un consenso.
const CONSENSI_FOTO = [
  ["foto_interne", "Riprese a uso interno", "Foto e video usati solo dentro l'oratorio per documentare le attività"],
  ["foto_parrocchia", "Bacheca e canali della parrocchia", "Pubblicazione sulla bacheca fisica e sui canali parrocchiali"],
  ["foto_web", "Sito, social e stampa", "Pubblicazione su internet e sui mezzi di informazione"],
];

const MAX_CONTATTI = 3;
const MAX_SANITARIE = 1000;
const MAX_ORGANIZZATIVE = 300;

const contattoVuoto = () => ({ nome: "", cognome: "", relazione: "", telefono: "" });

export default function AnimatoForm({
  initial,
  onSubmit,
  saving,
  isPublic = false,
  submitLabel = "Salva",
  wizard = false,
  account,
  accountMode = false,
  onAccountChange,
}) {
  const isWizard = wizard === true;
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState("");
  const contattoAutofillRef = useRef(null);
  const [form, setForm] = useState({
    nome: "", cognome: "", data_nascita: "", codice_fiscale: "", taglia_maglia: "",
    compilante: { nome: "", cognome: "", relazione: "", telefono: "" },
    dichiarazione_responsabilita: false,
    contatti_emergenza: [contattoVuoto()],
    info_sanitarie: "",
    esigenze_alimentari: [],
    esigenze_alimentari_altro: "",
    note_organizzative: "",
    // null = non espresso. Diverso da false, che è un rifiuto esplicito.
    consensi: { dati_sanitari: null, foto_interne: null, foto_parrocchia: null, foto_web: null },
    dichiarazione_altro_genitore: false,
    informativa_presa_visione: false,
    informativa_versione: null,
    informativa_hash: null,
    // Solo Segreteria
    informativa_consegnata_il: "",
    modulo_cartaceo_acquisito: false,
    quota_pagata: false, quota_pagata_data: "",
  });

  useEffect(() => {
    if (initial) {
      setForm((f) => ({
        ...f,
        nome: initial.nome || "", cognome: initial.cognome || "",
        data_nascita: initial.data_nascita || "", codice_fiscale: initial.codice_fiscale || "",
        taglia_maglia: initial.taglia_maglia || "",
        compilante: initial.compilante || f.compilante,
        contatti_emergenza: initial.contatti_emergenza?.length
          ? initial.contatti_emergenza.map((c) => ({ ...contattoVuoto(), ...c }))
          : [contattoVuoto()],
        info_sanitarie: initial.info_sanitarie || "",
        esigenze_alimentari: initial.esigenze_alimentari || [],
        esigenze_alimentari_altro: initial.esigenze_alimentari_altro || "",
        note_organizzative: initial.note_organizzative || "",
        consensi: Object.fromEntries(
          ["dati_sanitari", "foto_interne", "foto_parrocchia", "foto_web"].map((k) => [
            k, initial.consensi?.[k]?.valore ?? null,
          ])
        ),
        informativa_consegnata_il: initial.informativa_consegnata_il || "",
        modulo_cartaceo_acquisito: !!initial.modulo_cartaceo_acquisito,
        quota_pagata: !!initial.quota_pagata,
        quota_pagata_data: initial.quota_pagata_data || "",
      }));
    }
  }, [initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCompilante = (k, v) => setForm((f) => ({ ...f, compilante: { ...f.compilante, [k]: v } }));
  const setConsenso = (k, v) => setForm((f) => ({ ...f, consensi: { ...f.consensi, [k]: v } }));
  const setContatto = (i, k, v) =>
    setForm((f) => {
      const c = [...f.contatti_emergenza];
      c[i] = { ...c[i], [k]: v };
      return { ...f, contatti_emergenza: c };
    });

  // Al primo ingresso nel passo dei contatti proponiamo il genitore come
  // contatto principale. Il contatto resta sincronizzato con il passo 1,
  // dove soltanto l'utente può modificarne i dati.
  useEffect(() => {
    if (!isWizard || step !== 2) return;
    const genitore = form.compilante || {};
    const nome = (genitore.nome || "").trim();
    const cognome = (genitore.cognome || "").trim();
    const telefono = (genitore.telefono || "").trim();
    if (!nome && !cognome && !telefono) return;

    const relazione = ["madre", "padre", "tutore"].includes(genitore.relazione)
      ? genitore.relazione
      : "altro";
    const proposta = { nome, cognome, relazione, telefono };
    setForm((f) => {
      const primo = f.contatti_emergenza[0] || contattoVuoto();
      const vuoto = !primo.nome && !primo.cognome && !primo.relazione && !primo.telefono;
      const ugualeUltimoAutofill = contattoAutofillRef.current &&
        ["nome", "cognome", "relazione", "telefono"].every((k) => primo[k] === contattoAutofillRef.current[k]);
      if (!vuoto && !ugualeUltimoAutofill) return f;
      contattoAutofillRef.current = proposta;
      return { ...f, contatti_emergenza: [{ ...primo, ...proposta }, ...f.contatti_emergenza.slice(1)] };
    });
  }, [isWizard, step, form.compilante]);

  const toggleEsigenza = (k) =>
    setForm((f) => ({
      ...f,
      esigenze_alimentari: f.esigenze_alimentari.includes(k)
        ? f.esigenze_alimentari.filter((x) => x !== k)
        : [...f.esigenze_alimentari, k],
    }));

  const etaFuoriFascia = form.data_nascita && !etaAnimatoValida(form.data_nascita);
  const qualcheFoto = CONSENSI_FOTO.some(([k]) => form.consensi[k] === true);

  const wizardSteps = [
    ["Account e genitore", "I dati di chi presenta la richiesta"],
    ["Dati del minore", "Le informazioni necessarie per l'iscrizione"],
    ["Contatti di emergenza", "Chi possiamo chiamare se serve"],
    ["Salute e necessità", "Informazioni utili per vivere bene le attività"],
    ["Consensi e riepilogo", "Un ultimo controllo prima dell'invio"],
  ];

  const accountSet = (key, value) => onAccountChange?.({ ...(account || {}), [key]: value });
  const wizardStepClass = (target) => (isWizard && step !== target ? "hidden" : "");

  const validateStep = (target) => {
    let error = "";
    if (target === 0) {
      if (account && !accountMode) {
        if (!account.email?.trim() || !/^\S+@\S+\.\S+$/.test(account.email.trim())) error = "Inserisci un indirizzo email valido.";
        else if (!account.password || account.password.length < 8) error = "La password deve contenere almeno 8 caratteri.";
        else if (account.password !== account.passwordConferma) error = "Le password non coincidono.";
      }
      if (!error && (!(form.compilante.nome || "").trim() || !(form.compilante.cognome || "").trim() || !form.compilante.relazione || !(form.compilante.telefono || "").trim())) {
        error = "Completa tutti i dati del genitore o tutore.";
      }
      if (!error && isPublic && !form.dichiarazione_responsabilita) error = "Conferma di avere la responsabilità genitoriale sul minore.";
    } else if (target === 1) {
      if (!form.nome.trim() || !form.cognome.trim() || !form.data_nascita || !form.codice_fiscale.trim() || !form.taglia_maglia) {
        error = "Completa tutti i dati obbligatori del minore.";
      } else if (isPublic && etaFuoriFascia) {
        error = `L'iscrizione è riservata ai ${ETA_MIN_ANIMATO}–${ETA_MAX_ANIMATO} anni.`;
      }
    } else if (target === 2) {
      const primo = form.contatti_emergenza[0];
      if (!(primo?.nome || "").trim() || !(primo?.cognome || "").trim() || !primo?.relazione || !(primo?.telefono || "").trim()) {
        error = "Inserisci almeno un contatto di emergenza completo.";
      }
    } else if (target === 4) {
      if (qualcheFoto && isPublic && !form.dichiarazione_altro_genitore) error = "Conferma di aver informato l'altro genitore sui consensi fotografici.";
      else if (isPublic && !form.informativa_presa_visione) error = "Prendi visione dell'informativa privacy per continuare.";
    }
    setStepError(error);
    return !error;
  };

  const validateAll = () => {
    for (const target of [0, 1, 2, 4]) {
      if (!validateStep(target)) {
        setStep(target);
        return false;
      }
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStepError("");
    setStep((s) => Math.min(wizardSteps.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStepError("");
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Un contatto è "di un'altra persona" se il nominativo differisce da chi
  // compila: in quel caso va informata, e glielo deve dire chi compila.
  const contattoTerzo = form.contatti_emergenza.some(
    (c) =>
      (c.nome || c.cognome) &&
      `${c.nome} ${c.cognome}`.trim().toLowerCase() !==
        `${form.compilante.nome} ${form.compilante.cognome}`.trim().toLowerCase()
  );

  const submit = (e) => {
    e.preventDefault();
    if (isWizard && !validateAll()) return;
    if (isPublic && etaFuoriFascia) return;
    const payload = {
      nome: form.nome.trim(), cognome: form.cognome.trim(),
      data_nascita: form.data_nascita,
      codice_fiscale: form.codice_fiscale.trim().toUpperCase(),
      taglia_maglia: form.taglia_maglia,
      contatti_emergenza: form.contatti_emergenza.filter((c) => c.nome && c.telefono),
      compilante: form.compilante,
      info_sanitarie: form.info_sanitarie.trim() || null,
      esigenze_alimentari: form.esigenze_alimentari,
      esigenze_alimentari_altro: form.esigenze_alimentari_altro.trim() || null,
      note_organizzative: form.note_organizzative.trim() || null,
      consensi: form.consensi,
    };
    if (isPublic) {
      payload.dichiarazione_responsabilita = form.dichiarazione_responsabilita;
      payload.dichiarazione_altro_genitore = form.dichiarazione_altro_genitore;
      payload.informativa_presa_visione = form.informativa_presa_visione;
      payload.informativa_versione = form.informativa_versione;
      payload.informativa_hash = form.informativa_hash;
    } else {
      payload.informativa_consegnata_il = form.informativa_consegnata_il;
      payload.modulo_cartaceo_acquisito = form.modulo_cartaceo_acquisito;
      payload.quota_pagata = form.quota_pagata;
      payload.quota_pagata_data = form.quota_pagata ? form.quota_pagata_data || null : null;
    }
    onSubmit(payload);
  };

  const bloccato = isWizard ? saving : saving ||
    (isPublic &&
      (!form.informativa_presa_visione ||
        !form.dichiarazione_responsabilita ||
        etaFuoriFascia ||
        (qualcheFoto && !form.dichiarazione_altro_genitore))) ||
    (!isPublic && (!form.modulo_cartaceo_acquisito || !form.informativa_consegnata_il));

  return (
    <form onSubmit={submit} noValidate={isWizard} data-testid="animato-form">
      {isWizard && (
        <div className="mb-6" data-testid="animato-wizard-header">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-600">Passo {step + 1} di {wizardSteps.length}</p>
              <h2 className="font-display text-xl font-extrabold text-slate-900">{wizardSteps[step][0]}</h2>
              <p className="text-sm text-slate-500 font-semibold">{wizardSteps[step][1]}</p>
            </div>
            <span className="h-10 w-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-display font-extrabold">{step + 1}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-label={`Avanzamento: passo ${step + 1} di ${wizardSteps.length}`}>
            <div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{ width: `${((step + 1) / wizardSteps.length) * 100}%` }} />
          </div>
          {stepError && <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700" role="alert">{stepError}</p>}
        </div>
      )}
      {isWizard && step === 0 && account && !accountMode && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 mb-5" data-testid="genitore-account-box">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"><UserRound className="h-5 w-5" /></div>
            <div>
              <p className="font-display font-extrabold text-slate-900">Il tuo account</p>
              <p className="text-xs text-slate-600 font-semibold mt-0.5">Userai questa email per accedere dopo l'approvazione.</p>
            </div>
          </div>
          <label className="label-app">Email <span className="normal-case font-semibold text-rose-500">Obbligatorio</span></label>
          <input className="input-app mb-3" type="email" value={account.email || ""} onChange={(e) => accountSet("email", e.target.value)} placeholder="nome@esempio.it" autoCapitalize="none" autoCorrect="off" data-testid="genitore-email-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="label-app">Password</label><input className="input-app" type="password" minLength={8} value={account.password || ""} onChange={(e) => accountSet("password", e.target.value)} data-testid="genitore-password-input" /></div>
            <div><label className="label-app">Conferma password</label><input className="input-app" type="password" minLength={8} value={account.passwordConferma || ""} onChange={(e) => accountSet("passwordConferma", e.target.value)} data-testid="genitore-password-confirm-input" /></div>
          </div>
          <p className="flex items-start gap-2 text-xs text-slate-600 font-semibold mt-3"><ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />La richiesta viene attivata solo dopo la verifica della Segreteria.</p>
        </div>
      )}
      {/* ---------- CHI COMPILA ---------- */}
      <div className={`${wizardStepClass(0)} rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-5`} data-testid="blocco-compilante">
        <p className="font-bold text-slate-800 text-sm mb-1">
          {isPublic ? "Chi sta compilando il modulo" : "Chi ha fornito i dati"}
        </p>
        <p className="text-xs text-slate-500 font-semibold mb-3">
          Serve per sapere a chi rivolgersi e per verificare chi è legittimato a iscrivere il minore.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Nome">
            <input className="input-app" required={isPublic} value={form.compilante.nome}
              onChange={(e) => setCompilante("nome", e.target.value)}
              onBlur={(e) => setCompilante("nome", formattaNome(e.target.value))}
              data-testid="compilante-nome" />
          </Field>
          <Field label="Cognome">
            <input className="input-app" required={isPublic} value={form.compilante.cognome}
              onChange={(e) => setCompilante("cognome", e.target.value)}
              onBlur={(e) => setCompilante("cognome", formattaNome(e.target.value))}
              data-testid="compilante-cognome" />
          </Field>
          <Field label="In qualità di">
            <select className="input-app" required={isPublic} value={form.compilante.relazione}
              onChange={(e) => { setCompilante("relazione", e.target.value); blurSuTouch(e); }}
              data-testid="compilante-relazione">
              <option value="" disabled>Seleziona...</option>
              {RELAZIONI_COMPILANTE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Telefono">
            <input type="tel" className="input-app" required={isPublic} value={form.compilante.telefono}
              onChange={(e) => setCompilante("telefono", e.target.value)}
              data-testid="compilante-telefono" />
          </Field>
        </div>

        {isPublic && (
          <label className="flex items-start gap-3 cursor-pointer mt-3">
            <input type="checkbox" required className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
              checked={form.dichiarazione_responsabilita}
              onChange={(e) => set("dichiarazione_responsabilita", e.target.checked)}
              data-testid="dichiarazione-responsabilita" />
            <span className="text-xs text-slate-700 font-medium leading-relaxed">
              Dichiaro di essere esercente la responsabilità genitoriale sul minore e di agire
              anche in nome dell'altro genitore.
            </span>
          </label>
        )}
      </div>

      {/* ---------- DATI DEL MINORE ---------- */}
      <div className={wizardStepClass(1)} data-testid="wizard-step-minore">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nome">
          <input className="input-app" required value={form.nome} onChange={(e) => set("nome", e.target.value)} onBlur={(e) => set("nome", formattaNome(e.target.value))} data-testid="animato-nome-input" />
        </Field>
        <Field label="Cognome">
          <input className="input-app" required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} onBlur={(e) => set("cognome", formattaNome(e.target.value))} data-testid="animato-cognome-input" />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Data di nascita"
          hint={etaFuoriFascia
            ? (isPublic
                ? `L'iscrizione è riservata ai ${ETA_MIN_ANIMATO}–${ETA_MAX_ANIMATO} anni (compiuti al 1° luglio).`
                : `Attenzione: fuori dalla fascia ${ETA_MIN_ANIMATO}–${ETA_MAX_ANIMATO} anni al 1° luglio. Puoi comunque procedere.`)
            : undefined}>
          <input type="date" className={`input-app ${etaFuoriFascia ? "!border-amber-400" : ""}`} required value={form.data_nascita} onChange={(e) => set("data_nascita", e.target.value)} data-testid="animato-nascita-input" />
        </Field>
        <Field label="Taglia maglia" hint="Obbligatoria: serve per l'ordine delle maglie">
          <select className="input-app" required value={form.taglia_maglia}
            onChange={(e) => { set("taglia_maglia", e.target.value); blurSuTouch(e); }}
            data-testid="animato-taglia-select">
            <option value="" disabled>Seleziona taglia...</option>
            {TAGLIE.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Codice Fiscale"
        hint="Serve per il tesseramento e la copertura assicurativa del minore, ed è comunicato esclusivamente all'ente assicuratore e all'ente promotore per tali finalità.">
        <input className="input-app uppercase" required maxLength={16} value={form.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} data-testid="animato-cf-input" />
      </Field>
      </div>

      {/* ---------- CONTATTI DI EMERGENZA ---------- */}
      <div className={wizardStepClass(2)} data-testid="wizard-step-contatti">
      <label className="label-app">Contatti di emergenza (da 1 a {MAX_CONTATTI})</label>
      {isWizard && <p className="text-xs text-slate-500 font-semibold mb-3">Il primo contatto è il genitore che presenta la richiesta ed è modificabile dal passo 1. Puoi aggiungere un altro contatto se necessario.</p>}
      {form.contatti_emergenza.map((c, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 p-3 mb-2" data-testid={`contatto-${i}`}>
          <div className="flex items-start gap-2">
            <div className="grid grid-cols-2 gap-2 flex-1">
              <input className={`input-app ${isWizard && i === 0 ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} placeholder="Nome" required={i === 0} readOnly={isWizard && i === 0} value={c.nome} onChange={(e) => setContatto(i, "nome", e.target.value)} data-testid={`contatto-nome-${i}`} />
              <input className={`input-app ${isWizard && i === 0 ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} placeholder="Cognome" required={i === 0} readOnly={isWizard && i === 0} value={c.cognome} onChange={(e) => setContatto(i, "cognome", e.target.value)} data-testid={`contatto-cognome-${i}`} />
              <select className={`input-app ${isWizard && i === 0 ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} required={i === 0} disabled={isWizard && i === 0} value={c.relazione}
                onChange={(e) => { setContatto(i, "relazione", e.target.value); blurSuTouch(e); }}
                data-testid={`contatto-relazione-${i}`}>
                <option value="" disabled>Legame col minore...</option>
                {RELAZIONI_CONTATTO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input type="tel" className={`input-app ${isWizard && i === 0 ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} placeholder="Telefono" required={i === 0} readOnly={isWizard && i === 0} value={c.telefono} onChange={(e) => setContatto(i, "telefono", e.target.value)} data-testid={`contatto-telefono-${i}`} />
            </div>
            {form.contatti_emergenza.length > 1 && !(isWizard && i === 0) && (
              <button type="button" className="text-slate-400 hover:text-red-500 shrink-0 mt-2" onClick={() => set("contatti_emergenza", form.contatti_emergenza.filter((_, j) => j !== i))} data-testid={`contatto-rimuovi-${i}`}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
      {form.contatti_emergenza.length < MAX_CONTATTI && (
        <button type="button" className="text-xs font-bold text-sky-600 flex items-center gap-1 mb-3" onClick={() => set("contatti_emergenza", [...form.contatti_emergenza, contattoVuoto()])} data-testid="contatto-aggiungi-btn">
          <Plus className="h-3.5 w-3.5" /> Aggiungi contatto
        </button>
      )}

      {contattoTerzo && (
        <p className="flex items-start gap-2 text-xs font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-2xl p-3 mb-4" data-testid="avviso-contatto-terzo">
          <Info className="h-4 w-4 shrink-0 mt-px" />
          <span>
            Se indichi il recapito di un'altra persona, devi averla informata. Puoi consegnarle
            questa informativa:{" "}
            <a href="/informativa/contatti_emergenza" target="_blank" rel="noopener noreferrer" className="font-bold underline underline-offset-2" data-testid="link-informativa-contatti">
              informativa per i contatti di emergenza
            </a>.
          </span>
        </p>
      )}
      </div>

      {/* ---------- INFORMAZIONI SANITARIE (art. 9) ---------- */}
      <div className={wizardStepClass(3)} data-testid="wizard-step-necessita">
      <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 mb-4" data-testid="blocco-sanitario">
        <p className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-1">
          <HeartPulse className="h-4 w-4 text-rose-500" /> Informazioni sanitarie
        </p>
        <label className="block text-xs text-slate-600 font-semibold mb-2" htmlFor="info-sanitarie">
          Informazioni sanitarie rilevanti per la sicurezza (allergie, intolleranze, terapie in
          corso, patologie di cui gli operatori devono essere a conoscenza)
        </label>
        <textarea id="info-sanitarie" className="input-app" rows={3} maxLength={MAX_SANITARIE}
          value={form.info_sanitarie}
          onChange={(e) => set("info_sanitarie", e.target.value)}
          data-testid="info-sanitarie-input" />
        <p className="text-[11px] text-slate-500 font-semibold mt-1">
          {form.info_sanitarie.length}/{MAX_SANITARIE} caratteri
        </p>
        <p className="text-xs text-rose-800 bg-rose-100 border border-rose-200 rounded-xl p-3 font-semibold mt-2" data-testid="avviso-sanitario">
          Questa informazione sarà visibile solo ai Responsabili dell'oratorio e sarà cancellata
          al termine delle attività.
        </p>

        {form.info_sanitarie.trim() && (
          <label className="flex items-start gap-3 cursor-pointer mt-3">
            <input type="checkbox" className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
              checked={form.consensi.dati_sanitari === true}
              onChange={(e) => setConsenso("dati_sanitari", e.target.checked ? true : null)}
              data-testid="consenso-dati-sanitari" />
            <span className="text-xs text-slate-700 font-medium leading-relaxed">
              Acconsento al trattamento delle informazioni sanitarie indicate, per la sola
              finalità di far partecipare il minore alle attività in sicurezza.
            </span>
          </label>
        )}
        {form.info_sanitarie.trim() && form.consensi.dati_sanitari !== true && (
          <p className="flex items-start gap-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2" data-testid="avviso-sanitario-senza-consenso">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
            Senza questo consenso l'iscrizione viene comunque accettata, ma le informazioni
            sanitarie non verranno conservate e gli operatori non potranno tenerne conto.
          </p>
        )}
      </div>

      {/* ---------- ESIGENZE ALIMENTARI ---------- */}
      <Field label="Esigenze alimentari (facoltative)">
        <div className="flex flex-wrap gap-2 mt-1" data-testid="esigenze-alimentari">
          {ESIGENZE.map(([v, l]) => {
            const attiva = form.esigenze_alimentari.includes(v);
            return (
              <button key={v} type="button" onClick={() => toggleEsigenza(v)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors ${attiva ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}
                data-testid={`esigenza-${v}`}>
                {l}
              </button>
            );
          })}
        </div>
        {form.esigenze_alimentari.includes("altro") && (
          <input className="input-app mt-2" maxLength={200} placeholder="Specifica quale"
            value={form.esigenze_alimentari_altro}
            onChange={(e) => set("esigenze_alimentari_altro", e.target.value)}
            data-testid="esigenza-altro-input" />
        )}
      </Field>

      {/* ---------- NOTE ORGANIZZATIVE ---------- */}
      <Field label="Note organizzative (facoltative)"
        hint="Es. chi ritira il bambino. Non inserire qui informazioni sulla salute: usa il campo dedicato sopra.">
        <textarea className="input-app" rows={2} maxLength={MAX_ORGANIZZATIVE}
          value={form.note_organizzative}
          onChange={(e) => set("note_organizzative", e.target.value)}
          data-testid="note-organizzative-input" />
        <p className="text-[11px] text-slate-400 font-semibold mt-1">
          {form.note_organizzative.length}/{MAX_ORGANIZZATIVE} caratteri
        </p>
      </Field>
      </div>

      {/* ---------- CONSENSI FOTOGRAFICI ---------- */}
      <div className={wizardStepClass(4)} data-testid="wizard-step-consensi">
      <div className="rounded-2xl border border-slate-200 p-4 mb-4" data-testid="blocco-consensi-foto">
        <p className="font-bold text-slate-800 text-sm mb-1">Fotografie e riprese</p>
        <p className="text-xs text-slate-500 font-semibold mb-3">
          Ogni voce è indipendente e facoltativa. Non concederne nessuna non impedisce la
          partecipazione alle attività.
        </p>
        {CONSENSI_FOTO.map(([k, titolo, spiega]) => (
          <label key={k} className="flex items-start gap-3 cursor-pointer py-2 border-t border-slate-100 first:border-t-0">
            <input type="checkbox" className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
              checked={form.consensi[k] === true}
              onChange={(e) => setConsenso(k, e.target.checked ? true : null)}
              data-testid={`consenso-${k}`} />
            <span className="min-w-0">
              <span className="block text-sm font-bold text-slate-700">{titolo}</span>
              <span className="block text-xs text-slate-500 font-semibold">{spiega}</span>
            </span>
          </label>
        ))}

        {qualcheFoto && isPublic && (
          <label className="flex items-start gap-3 cursor-pointer mt-3 pt-3 border-t border-slate-200">
            <input type="checkbox" className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
              checked={form.dichiarazione_altro_genitore}
              onChange={(e) => set("dichiarazione_altro_genitore", e.target.checked)}
              data-testid="dichiarazione-altro-genitore" />
            <span className="text-xs text-slate-700 font-medium leading-relaxed">
              Dichiaro che l'altro genitore è a conoscenza e concorda con i consensi sopra prestati.
            </span>
          </label>
        )}
      </div>

      {/* ---------- SEGRETERIA: adempimento cartaceo ---------- */}
      {!isPublic && (
        <>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-4" data-testid="blocco-cartaceo">
            <p className="font-bold text-slate-800 text-sm mb-1">Adempimento dell'informativa</p>
            <p className="text-xs text-slate-600 font-semibold mb-3">
              Per un'iscrizione registrata al banco servono gli estremi della consegna
              dell'informativa: senza, non resta alcuna prova dell'adempimento.
            </p>
            <Field label="Informativa consegnata il">
              <input type="date" className="input-app" required value={form.informativa_consegnata_il}
                onChange={(e) => set("informativa_consegnata_il", e.target.value)}
                data-testid="informativa-consegnata-il" />
            </Field>
            <label className="flex items-start gap-3 cursor-pointer mt-3">
              <input type="checkbox" required className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
                checked={form.modulo_cartaceo_acquisito}
                onChange={(e) => set("modulo_cartaceo_acquisito", e.target.checked)}
                data-testid="modulo-cartaceo-acquisito" />
              <span className="text-xs text-slate-700 font-medium leading-relaxed">
                Ho acquisito il modulo cartaceo firmato dall'esercente la responsabilità genitoriale.
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Quota di iscrizione">
              <div className="flex items-center gap-2 mt-1">
                <input type="checkbox" id="quota" className="h-5 w-5 rounded accent-emerald-500" checked={form.quota_pagata} onChange={(e) => set("quota_pagata", e.target.checked)} data-testid="animato-quota-checkbox" />
                <label htmlFor="quota" className="text-sm font-semibold text-slate-700">Pagata</label>
              </div>
              {form.quota_pagata && (
                <input type="date" className="input-app mt-2" value={form.quota_pagata_data} onChange={(e) => set("quota_pagata_data", e.target.value)} data-testid="animato-quota-data-input" />
              )}
            </Field>
          </div>
        </>
      )}

      {/* ---------- PRESA VISIONE ---------- */}
      {isPublic && (
        <InformativaBox
          tipo="iscritti"
          valore={form.informativa_presa_visione}
          etichetta="Il genitore o chi esercita la responsabilità genitoriale dichiara di aver letto l'informativa sul trattamento dei dati del minore."
          onChange={({ presaVisione, versione, hash }) =>
            setForm((f) => ({
              ...f,
              informativa_presa_visione: presaVisione,
              informativa_versione: versione,
              informativa_hash: hash,
            }))
          }
          testId="informativa-iscritti"
        />
      )}

      {isWizard && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 mb-4" data-testid="wizard-riepilogo">
          <p className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" />Prima di inviare</p>
          <p className="text-xs text-slate-600 font-semibold leading-relaxed">Controlla che i dati siano corretti. Dopo l'invio la richiesta verrà verificata dalla Segreteria.</p>
        </div>
      )}
      </div>

      {isWizard && step < wizardSteps.length - 1 ? (
        <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-slate-100">
          <button type="button" className="btn-ghost !py-2.5" onClick={goBack} disabled={step === 0} data-testid="animato-wizard-indietro"><ArrowLeft className="h-4 w-4" /> Indietro</button>
          <button type="button" className="btn-primary !py-2.5" onClick={goNext} data-testid="animato-wizard-continua">Continua <ArrowRight className="h-4 w-4" /></button>
        </div>
      ) : isWizard ? (
        <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-slate-100">
          <button type="button" className="btn-ghost !py-2.5" onClick={goBack} data-testid="animato-wizard-indietro-finale"><ArrowLeft className="h-4 w-4" /> Indietro</button>
          <button type="submit" className="btn-primary !py-2.5 flex-1" disabled={bloccato} data-testid="animato-submit-btn">
            {saving ? "Salvataggio..." : submitLabel}
          </button>
        </div>
      ) : (
        <button type="submit" className="btn-primary w-full mt-2" disabled={bloccato} data-testid="animato-submit-btn">
          {saving ? "Salvataggio..." : submitLabel}
        </button>
      )}
    </form>
  );
}
